import type { ReactNode } from 'react'
import { Link, Navigate, useLocation } from 'react-router'
import { useAuth } from '@/lib/auth/store'
import { getSubdomain } from '@/lib/org'
import { meetsPlan, type MinPlan } from '@/lib/plan'
import { useOrg } from '@/lib/useOrg'

/** 로그인 필요. 미인증이면 로그인으로 보내되 원래 목적지를 기억한다. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { accessToken, user } = useAuth()
  const location = useLocation()

  if (!accessToken || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  /*
   * 🔴 로그인은 됐는데 회사 코드가 없는 상태에서 빠져나갈 길이 없었다 (2026-09-07).
   *
   *   회사 코드를 묻는 화면은 `Login.tsx` 안에만 있다(`needsOrgCode`). 로그인이
   *   돼 있으면 거기를 안 지나므로, 조직이 없어도 물어볼 자리가 없다. 모든 API 가
   *   서버에서 404 "조직을 찾을 수 없습니다" 를 받고 화면은 그걸 빨간 글씨로 그리고
   *   끝낸다. 새로고침해도 같다.
   *
   *   실제로 났다 — 3단계에서 회사 코드 키만 이름이 바뀌고(`biznomi_org_code` →
   *   `biznomi_mtt_org_code`) 토큰 키는 그대로여서, **로그인은 살고 조직만 사라진**
   *   반쪽 상태가 됐다. "재로그인 한 번이면 된다" 고 봤는데 로그인이 안 풀려서
   *   회사 코드를 다시 물을 기회조차 없었다.
   *
   *   로그인으로 보낸다. 거기 회사 코드 입력 화면이 이미 있다.
   */
  if (getSubdomain() === '') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  return <>{children}</>
}

/**
 * 관리자 전용. MANAGER 도 통과시키되, 보안 메뉴처럼 ADMIN 만 되는 곳은
 * RequireAdmin 을 따로 쓴다 (보안 알림 = ADMIN 전용, 명세 §16-D15).
 */
export function RequireStaff({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'ADMIN' && user.role !== 'MANAGER') return <Navigate to="/" replace />
  return <>{children}</>
}

/** 최종관리자(ADMIN) 전용 — 감사로그·이상탐지·체인검증 등 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'ADMIN') return <Navigate to="/" replace />
  return <>{children}</>
}

/** 이미 로그인했으면 로그인 화면 대신 홈으로 */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { accessToken, user } = useAuth()

  /*
   * 🔴 회사 코드가 없으면 되돌려 보내지 않는다 (2026-09-07).
   *   `RequireAuth` 가 조직 없는 로그인 상태를 여기로 보내는데, 여기서 다시
   *   `/` 로 튕기면 **무한 고리**가 되고 화면이 하얗게 남는다. 실제로 그랬다.
   *   회사 코드를 받아야 하니 로그인 화면(`needsOrgCode` 분기)에 머문다.
   */
  if (accessToken && user && getSubdomain() !== '') return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * 플랜 게이트 — **막되, 파는 자리로 쓴다** (2026-09-09).
 *
 * 🔴 라우트에 플랜 가드가 하나도 없었다. `minPlan` 은 메뉴에만 있어서, 주소를 직접
 *   치면 BASIC 조직도 PRO 화면이 그대로 그려졌다. 서버는 403 인데
 *   (`{"error":"이 기능은 PRO 플랜 이상에서 사용 가능합니다"}` — 실측) 화면은
 *   빈 목록을 받고 **«급여명세서가 없습니다 — «명세서 보내기» 로 시작하세요»** 라고 했다.
 *   **없는 게 아니라 못 쓰는 것**인데 «데이터가 없다» 로 그렸다 (캡처 13).
 *
 * 만나는 사람: 주소를 아는 사람 · PRO→BASIC 강등 · 체험 중 둘러보는 사람.
 * 마지막이 제일 많다. 그 사람에게 «비었습니다» 라고 하면 **파는 물건이 안 보인다.**
 *
 * ⚠ 화면은 거들 뿐이다. 막는 건 서버(`requirePlan`)다.
 * ⚠ 판정 규칙은 `lib/plan.ts` 한 곳이다. 메뉴(`AppShell`)와 같은 함수를 쓴다 —
 *   두 곳에 적으면 다음에 한쪽만 고쳐진다.
 */
export function RequirePlan({
  min,
  what,
  children,
}: {
  min: MinPlan
  /** «PRO를 쓰면 ○○» 의 ○○. 문장을 끝맺는 말까지 넣는다 */
  what: string
  children: ReactNode
}) {
  const { data: org, isLoading } = useOrg()

  // 조직 정보를 받기 전에는 아무것도 안 그린다. 잠깐 안내가 떴다 사라지면 더 나쁘다
  if (isLoading) return null
  if (meetsPlan(org?.plan, min)) return <>{children}</>

  /*
   * ⚠ `<main>` 을 여기서 또 쓰지 않는다. `AppShell` 이 이미 `Outlet` 을 `<main>` 으로
   *   감싼다 — 중첩하면 문서에 «본문» 이 둘이 되고 스크린리더가 둘로 센다.
   *   (`Login`·`NotFound`·`ChangePassword` 의 `<main>` 은 AppShell 밖이라 정상이다)
   *
   * ⚠ «{min}를» 의 조사는 `MinPlan` 이 받침 없는 두 값(PRO·ENTERPRISE)뿐이라 성립한다.
   *   받침 있는 이름을 그 타입에 넣는 날 이 줄이 조용히 어긋난다. 그때는 조사를
   *   문자열에서 빼고 문장을 다시 짠다.
   */
  return (
    <div className="mx-auto w-full max-w-lg px-5 py-16 text-center">
      <div className="rounded-card bg-white p-8">
        <p className="text-sm font-semibold text-[var(--color-primary)]">{min} 기능입니다</p>
        <h1 className="mt-2 text-xl font-bold">{min}를 쓰면 {what}</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          {/*
            🔴 화면에서 판매사를 부르는 자리가 여기뿐이다. 조직 로고·색으로 브랜딩된
              화면이라 고객사 관리자는 «메디펀» 이라는 이름을 모를 수 있다.
              개인정보 동의의 «주식회사 메디펀(비즈노미)» 은 법적 고지라 그대로 둔다.
          */}
          지금 회사는 <b>{org?.plan ?? 'BASIC'}</b> 입니다. 이 기능을 켜려면 비즈노미 본사에 문의해 주세요.
        </p>
        <Link
          to="/"
          className="mt-6 inline-block rounded-lg bg-[var(--color-primary)] px-5 py-2.5 text-sm font-semibold text-white"
        >
          홈으로
        </Link>
      </div>
    </div>
  )
}
